import { useState, useCallback } from "react";
import { calculateBenefits } from '@sailpoint/bva-core';
import InputForm from "./InputForm";
import { bvaInputs, fetchExchangeRate } from "../libs/store.js";
import ResultsDisplay from "./ResultsDisplay";

const Page = () => {

    const [inputs, setInputs] = useState(bvaInputs);
    const [currencyRate, setCurrencyRate] = useState({ currency_iso_code: 'USD', currency_conversion_rate: 1.0 });
    const [isFetchingRate, setIsFetchingRate] = useState(false);
    const [rateError, setRateError] = useState(null);
    const [calculationError, setCalculationError] = useState(null);
    const [calculationResults, setCalculationResults] = useState(null);
    const [inputsHaveChanged, setInputsHaveChanged] = useState(true);
    const [isCalculating, setIsCalculating] = useState(false);

    const handleInputChange = (newInputs) => {
        setInputs(newInputs);
        setInputsHaveChanged(true);
    };

    const handleCurrencyChange = useCallback(async (isoCode) => {
        setIsFetchingRate(true);
        setRateError(null);
        setInputsHaveChanged(true);
        try {
            const rate = await fetchExchangeRate(isoCode);
            setCurrencyRate({ currency_iso_code: isoCode, currency_conversion_rate: rate });
        } catch (err) {
            console.error('Failed to fetch exchange rate:', err);
            setRateError(`Could not fetch rate for ${isoCode}. Breach cost conversion will use 1:1 rate.`);
            setCurrencyRate({ currency_iso_code: isoCode, currency_conversion_rate: 1.0 });
        } finally {
            setIsFetchingRate(false);
        }
    }, []);

    const applyCurrencyRate = (usdResults, currencyRate) => {
        return {
            ...usdResults,
            fiveYearTotalSavings: usdResults.fiveYearTotalSavings * currencyRate,
            savingsBreakdown: usdResults.savingsBreakdown.map((row) => ({
                ...row,
                y1: row.y1 * currencyRate,
                y2: row.y2 * currencyRate,
                y3: row.y3 * currencyRate,
                y4: row.y4 * currencyRate,
                y5: row.y5 * currencyRate,
                total: row.total * currencyRate,
                // percentageImprovement stays a percent
            })),
            donutChartData: usdResults.donutChartData.map((d) => ({
                ...d,
                savings: d.savings * currencyRate,
            })),
            barChartData: usdResults.barChartData.map((d) => ({
                ...d,
                savings: d.savings * currencyRate,
            })),
        };
    }

    const runCalculations = async () => {
        setIsCalculating(true);
        setCalculationError(null);
        setCalculationResults(null);
        try {
            await new Promise(resolve => setTimeout(resolve, 100));
            const rawResults = calculateBenefits(inputs, {
                ...currencyRate,
                currency_conversion_rate: 1, // keep package math in USD
            });
            const rate = currencyRate.currency_conversion_rate;
            const convertedResults = applyCurrencyRate(rawResults, rate);
            setCalculationResults(convertedResults);
            setInputsHaveChanged(false);
        } catch (err) {
            console.error("Calculation Error:", err);
            const message = err instanceof Error ? err.message : "An unknown error occurred during calculation.";
            setCalculationError(message);
            setCalculationResults(null);
        } finally {
            setIsCalculating(false);
        }
    };

    const noBenefitsSelected = inputs.desired_benefits.length === 0;
    const isCalculateDisabled = isCalculating || isFetchingRate || !inputsHaveChanged || noBenefitsSelected;

    return (
        <>
            <div className="bg-white p-6 md:p-10 rounded-lg mb-8">

                <InputForm
                    onChange={handleInputChange}
                    onCurrencyChange={handleCurrencyChange}
                    exchangeRate={currencyRate.currency_conversion_rate}
                    isFetchingRate={isFetchingRate}
                    rateError={rateError}
                />

                <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                    {calculationError && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                            <strong className="font-bold">Calculation Error:</strong>
                            <span className="block sm:inline">{calculationError}</span>
                        </div>
                    )}

                    <button
                        onClick={runCalculations}
                        disabled={isCalculateDisabled}
                        className={`relative px-6 py-2 w-3xs rounded-md shadow text-white font-semibold overflow-hidden bg-gradient-to-r from-blue-900 to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 group ${isCalculateDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                        <span className="absolute inset-0 bg-gradient-to-r from-[#d60eb5] to-[#f06dff] opacity-0 transition-opacity duration-300 ease-in-out z-10 group-hover:opacity-100"></span>
                        <span className="relative z-20">
                            {isFetchingRate ? 'Loading rate...' : isCalculating ? 'Calculating...' : 'Calculate Benefits'}
                        </span>
                    </button>
                    {noBenefitsSelected && (
                        <div className="text-xs text-red-600 mt-1" role="alert">
                            <p>Please select one or more benefits before calculating.</p>
                            <span className="block sm:inline">{calculationError}</span>
                        </div>
                    )}
                </div>
            </div>
            {(calculationResults || isCalculating || calculationError) && !inputsHaveChanged && (
                <ResultsDisplay calculationResults={calculationResults} />
            )}
        </>
    );
};

export default Page;
